import fetchApiWithAuth from "./fetch";

export type IntegrationSheetsConfig = {
  marketplace_auth_id: number;
  marketplace: string;
  service_account_configured: boolean;
  sheets_list_enabled: boolean;
  sheets_list_url: string | null;
  sheets_list_gid: string | null;
  sheets_min_price: number | null;
  sheets_last_sync_at: string | null;
};

export type SkippedSheetRow = {
  article: string;
  reason: string;
};

export type IntegrationSheetsPreview = {
  rows: {
    article: string;
    fbsAction: 'enable' | 'disable';
    wbPrice: number | null;
    fbsThreshold: number | null;
  }[];
  toEnable: IntegrationSheetsPreview['rows'];
  toDisable: IntegrationSheetsPreview['rows'];
  articles: string[];
  hasPriceColumn: boolean;
  hasFbsStatusColumn: boolean;
  skipped: SkippedSheetRow[];
  skippedBelowMin: string[];
  belowPriceThreshold: IntegrationSheetsPreview['rows'];
  orphanDisables: string[];
  missing_in_catalog?: string[];
  missingInCatalog?: string[];
  sample: string[];
  total: number;
};

export type IntegrationSheetsSyncResult = {
  ok: boolean;
  marketplace_auth_id: number;
  marketplace: string;
  trigger: string;
  dry_run: boolean;
  rows_in_sheet: number;
  rows_eligible: number;
  rows_added: number;
  enabled_fbs: number;
  set_in_sale_on: number;
  set_in_sale_off: number;
  skipped_below_min: string[];
  below_price_threshold: string[];
  orphan_disables: string[];
  skipped_rows: SkippedSheetRow[];
  patch_errors: { article: string; error: string }[];
  missing_in_catalog: string[];
  in_sale_not_applied: string[];
  changes: { article: string; from: boolean; to: boolean }[];
  has_fbs_status_column: boolean;
  skipped?: string;
  error?: string;
};

export type IntegrationSheetsLogItem = {
  id: number;
  marketplace: string;
  kind: string;
  asOf: string;
  finishedAt: string;
  rowsChanged: number;
  rowsTotal: number;
  stats?: Record<string, unknown> | null;
};

export type IntegrationSheetsLogs = {
  since: string;
  hours: number;
  items: IntegrationSheetsLogItem[];
};

export type IntegrationSheetsUpdateDto = {
  sheets_list_enabled?: boolean;
  sheets_list_url?: string | null;
  sheets_list_gid?: string | null;
  sheets_min_price?: number | null;
};

export type SheetsSyncLocks = {
  wildberries: boolean;
  ozon: boolean;
};

export const SHEETS_SYNC_IN_SALE_TOOLTIP =
  'Управляется автосинхронизацией из Google Таблицы (вкладка «Таблица FBS» в интеграции)';

export function isInSaleLockedBySheetsSync(
  locks: SheetsSyncLocks,
  marketplace: 'ozon' | 'wildberries' | 'both',
): boolean {
  if (marketplace === 'both') {
    return locks.wildberries || locks.ozon;
  }
  return locks[marketplace];
}

export async function getSheetsSyncLocks(): Promise<SheetsSyncLocks> {
  const res = await fetchApiWithAuth('/api/markerplace-auth/sheets-sync-locks');
  if (!res.ok) {
    return { wildberries: false, ozon: false };
  }
  return res.json();
}

export async function getIntegrationSheetsConfig(
  marketplaceAuthId: number
): Promise<IntegrationSheetsConfig> {
  const res = await fetchApiWithAuth(
    `/api/markerplace-auth/${marketplaceAuthId}/sheets`
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Не удалось загрузить настройки таблицы");
  }
  return res.json();
}

export async function updateIntegrationSheetsConfig(
  marketplaceAuthId: number,
  body: IntegrationSheetsUpdateDto
): Promise<IntegrationSheetsConfig> {
  const res = await fetchApiWithAuth(
    `/api/markerplace-auth/${marketplaceAuthId}/sheets`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Не удалось сохранить настройки");
  }
  return res.json();
}

export async function previewIntegrationSheets(
  marketplaceAuthId: number
): Promise<IntegrationSheetsPreview> {
  const res = await fetchApiWithAuth(
    `/api/markerplace-auth/${marketplaceAuthId}/sheets/preview`,
    { method: "POST", body: "{}" }
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Не удалось прочитать таблицу");
  }
  return res.json();
}

export async function syncIntegrationSheets(
  marketplaceAuthId: number,
  dryRun = false
): Promise<IntegrationSheetsSyncResult> {
  const res = await fetchApiWithAuth(
    `/api/markerplace-auth/${marketplaceAuthId}/sheets/sync`,
    {
      method: "POST",
      body: JSON.stringify({ dry_run: dryRun }),
    }
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Синхронизация не удалась");
  }
  return res.json();
}

export async function getIntegrationSheetsLogs(
  marketplaceAuthId: number,
  hours = 72
): Promise<IntegrationSheetsLogs> {
  const res = await fetchApiWithAuth(
    `/api/markerplace-auth/${marketplaceAuthId}/sheets/logs?hours=${hours}`
  );
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || "Не удалось загрузить журнал");
  }
  return res.json();
}

export async function getIntegrationSheetsLogDetail(
  logId: number
): Promise<IntegrationSheetsLogItem> {
  const res = await fetchApiWithAuth(`/api/mp-sync-watermarks/logs/${logId}`);
  if (!res.ok) {
    throw new Error("Не удалось загрузить детали прогона");
  }
  return res.json();
}
